import { useState } from "react";
import {
    Title, Group, Select, Stack, Text, Card, Badge, Table, Code, Collapse, Button,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auditApi, type AuditLogEntry } from "../../api/audit.js";
import { apiFetch } from "../../api/client.js";
import { formatAuditTimestamp } from "../../i18n/format-date.js";
import { useLocale } from "../../i18n/use-locale.js";
import { useEnumLabel } from "../../i18n/enum-label.js";

// ---- Constants ----------------------------------------------------------------

// The audit_log.entity column (apps/api/src/db/schema.ts) has no enum
// constraint — it's whatever string literal each API module's routes.ts
// happens to write. This list is the app's one place that enumerates them;
// enum-label.test.ts checks it against the literals the API actually writes,
// so a new entity fails a test instead of rendering as a raw token here.
const ENTITY_VALUES = [
    "ereignis", "risiko", "massnahme", "bedrohung", "asset", "document",
    "likelihood_level", "damage_level", "user", "attachment", "session",
    "risk_massnahme", "massnahme_ereignis",
] as const;

// Must stay in step with the audit_log `op` enum in apps/api/src/db/schema.ts;
// enum-label.test.ts compares the two. "read" is written only for vertraulich
// document opens (modules/docs/routes.ts) — the confidential access trail, and
// the entry an auditor is most likely to filter for.
const OP_VALUES = ["create", "update", "delete", "read"] as const;

const PAGE_SIZE_OPTIONS = [
    { value: "25", label: "25" },
    { value: "50", label: "50" },
    { value: "100", label: "100" },
    { value: "200", label: "200" },
    { value: "500", label: "500" },
];

const OP_COLOR: Record<string, string> = {
    create: "blue",
    update: "yellow",
    delete: "red",
    // Grey on purpose rather than by falling through the ?? below: a read
    // changed nothing, and should not compete with the mutations for attention
    // while scanning the table.
    read: "gray",
};

const DEFAULT_PAGE_SIZE = 100;

// ---- Row component ------------------------------------------------------------

interface AuditRowProps {
    entry: AuditLogEntry;
    userNames: Map<number, string>;
    expanded: boolean;
    onToggle: () => void;
}

function AuditRow({ entry, userNames, expanded, onToggle }: AuditRowProps) {
    const { t } = useTranslation();
    const locale = useLocale();
    const { label } = useEnumLabel();
    const userName = entry.userId != null ? (userNames.get(entry.userId) ?? `#${entry.userId}`) : "–";
    return (
        <>
            <Table.Tr onClick={onToggle} style={{ cursor: "pointer" }}>
                <Table.Td>
                    <Text size="sm" ff="monospace">{formatAuditTimestamp(entry.at, locale)}</Text>
                </Table.Td>
                <Table.Td>
                    <Text size="sm">{label("auditEntity", entry.entity)}</Text>
                </Table.Td>
                <Table.Td>
                    <Text size="sm" c="dimmed">{entry.entityId ?? "–"}</Text>
                </Table.Td>
                <Table.Td>
                    <Badge color={OP_COLOR[entry.op] ?? "gray"} variant="light">
                        {label("auditOp", entry.op)}
                    </Badge>
                </Table.Td>
                <Table.Td>
                    <Text size="sm">{userName}</Text>
                </Table.Td>
            </Table.Tr>
            {expanded && (
                <Table.Tr>
                    <Table.Td colSpan={5} style={{ padding: 0 }}>
                        <Collapse in={expanded}>
                            <Stack gap={4} p="sm">
                                {entry.beforeJson && (
                                    <>
                                        <Text size="xs" fw={600} c="dimmed">{t("audit.detail.before")}</Text>
                                        <Code block style={{ whiteSpace: "pre-wrap", fontSize: 11 }}>
                                            {JSON.stringify(JSON.parse(entry.beforeJson), null, 2)}
                                        </Code>
                                    </>
                                )}
                                {entry.afterJson && (
                                    <>
                                        <Text size="xs" fw={600} c="dimmed">{t("audit.detail.after")}</Text>
                                        <Code block style={{ whiteSpace: "pre-wrap", fontSize: 11 }}>
                                            {JSON.stringify(JSON.parse(entry.afterJson), null, 2)}
                                        </Code>
                                    </>
                                )}
                                {!entry.beforeJson && !entry.afterJson && (
                                    <Text size="xs" c="dimmed">{t("audit.detail.empty")}</Text>
                                )}
                            </Stack>
                        </Collapse>
                    </Table.Td>
                </Table.Tr>
            )}
        </>
    );
}

// ---- Page component -----------------------------------------------------------

export function AuditLogPage() {
    const { t } = useTranslation();
    const { label } = useEnumLabel();
    const [entity, setEntity] = useState("");
    const [op, setOp] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const qc = useQueryClient();

    const entityOptions = [
        { value: "", label: t("audit.list.filter.allEntities") },
        ...ENTITY_VALUES.map((value) => ({ value, label: label("auditEntity", value) })),
    ];
    const opOptions = [
        { value: "", label: t("audit.list.filter.allOperations") },
        ...OP_VALUES.map((value) => ({ value, label: label("auditOp", value) })),
    ];

    const params = {
        entity: entity || undefined,
        op: op || undefined,
        page,
        pageSize,
    };

    // The only list in this app whose contents change because of *other people's*
    // actions, so it is the only one that cannot be kept current by invalidating
    // after the mutation that changed it: it ran once on mount and never again.
    //
    // Deliberately not polled. An audit log that reloads under the reader while
    // they are looking at it is worse than one that waits to be asked — rows
    // shift as new ones arrive, and the reader cannot tell a quiet system from a
    // stalled view either way. The button below is the whole mechanism, and it
    // is honest: nothing changes unless you ask.
    const { data, isLoading, isFetching, refetch } = useQuery({
        queryKey: ["audit", params],
        queryFn: () => auditApi.list(params),
    });

    // Load users list so we can display names
    const { data: usersData } = useQuery({
        queryKey: ["users"],
        queryFn: () => apiFetch<{ items: Array<{ id: number; name: string }> }>("/api/users"),
        staleTime: 60_000,
    });

    const userNames = new Map<number, string>(
        (usersData?.items ?? []).map((u) => [u.id, u.name])
    );

    // The op filter goes to the server with the query (params above). It used
    // to be applied here instead, to `data.items` — which narrowed one page of
    // an unfiltered result, so a filtered view showed only the matches that
    // happened to fall on the current page while `total` below still counted
    // every row.
    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const toggleRow = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

    const handleEntityChange = (val: string | null) => {
        setEntity(val ?? "");
        setPage(1);
        setExpandedId(null);
    };

    const handleOpChange = (val: string | null) => {
        setOp(val ?? "");
        setPage(1);
        setExpandedId(null);
    };

    const handlePageSizeChange = (val: string | null) => {
        setPageSize(Number(val) || DEFAULT_PAGE_SIZE);
        setPage(1);
        setExpandedId(null);
    };

    return (
        <Stack>
            <Title order={2}>{t("audit.list.title")}</Title>

            {/* Toolbar */}
            <Card withBorder padding="xs">
                <Group>
                    <Select
                        data={entityOptions}
                        value={entity}
                        onChange={handleEntityChange}
                        style={{ width: 200 }}
                        placeholder={t("audit.list.filter.allEntities")}
                    />
                    <Select
                        data={opOptions}
                        value={op}
                        onChange={handleOpChange}
                        style={{ width: 160 }}
                        placeholder={t("audit.list.filter.allOperations")}
                    />
                    {/*
                      The users lookup is invalidated alongside the audit query
                      so a brand-new account resolves to a name rather than to
                      #id — it is cached for a minute, and the row that would
                      name it is usually the reason somebody is refreshing.
                    */}
                    <Button
                        variant="default"
                        leftSection={<IconRefresh size={16} />}
                        loading={isFetching}
                        onClick={() => {
                            void refetch();
                            void qc.invalidateQueries({ queryKey: ["users"] });
                        }}
                    >
                        {t("audit.list.action.refresh")}
                    </Button>
                    <Group gap="xs" ml="auto">
                        <Text size="sm" c="dimmed">
                            {total > 0 ? t("common.entriesCount", { count: total }) : ""}
                        </Text>
                        <Select
                            data={PAGE_SIZE_OPTIONS}
                            value={String(pageSize)}
                            onChange={handlePageSizeChange}
                            style={{ width: 90 }}
                            label={undefined}
                            aria-label={t("audit.list.pageSizeLabel")}
                        />
                        <Group gap={4}>
                            <Text
                                size="sm"
                                style={{ cursor: page > 1 ? "pointer" : "default", userSelect: "none" }}
                                c={page > 1 ? undefined : "dimmed"}
                                onClick={() => { if (page > 1) { setPage(page - 1); setExpandedId(null); } }}
                            >
                                ‹
                            </Text>
                            <Text size="sm">{page} / {totalPages}</Text>
                            <Text
                                size="sm"
                                style={{ cursor: page < totalPages ? "pointer" : "default", userSelect: "none" }}
                                c={page < totalPages ? undefined : "dimmed"}
                                onClick={() => { if (page < totalPages) { setPage(page + 1); setExpandedId(null); } }}
                            >
                                ›
                            </Text>
                        </Group>
                    </Group>
                </Group>
            </Card>

            {/* Table */}
            <Card withBorder padding={0}>
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ width: 160 }}>{t("audit.list.column.timestamp")}</Table.Th>
                            <Table.Th>{t("audit.list.column.entity")}</Table.Th>
                            <Table.Th style={{ width: 80 }}>{t("audit.list.column.id")}</Table.Th>
                            <Table.Th style={{ width: 110 }}>{t("audit.list.column.operation")}</Table.Th>
                            <Table.Th>{t("audit.list.column.user")}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {isLoading && (
                            <Table.Tr>
                                <Table.Td colSpan={5}>
                                    <Text ta="center" c="dimmed" py="md">{t("audit.list.loading")}</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {!isLoading && items.length === 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={5}>
                                    <Text ta="center" c="dimmed" py="md">{t("audit.list.empty")}</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {items.map((entry) => (
                            <AuditRow
                                key={entry.id}
                                entry={entry}
                                userNames={userNames}
                                expanded={expandedId === entry.id}
                                onToggle={() => toggleRow(entry.id)}
                            />
                        ))}
                    </Table.Tbody>
                </Table>
            </Card>
        </Stack>
    );
}
